<template>
  <SlideIn
    :open="isOpen"
    :lock-scroll="false"
    heading="Size Guide"
    :content-padding="false"
    :wide-panel="false"
    max-width="500px"
    @close="isOpen = false"
  >
    <template #content>
      <div class="content-container-inner">
        <!-- Tabs: Product Measurements | Size Conversion (non-jewellery only) -->
        <div v-if="!isJewellery" class="sizeguide-tabs">
          <button
            type="button"
            :class="[
              'sizeguide-tab',
              { active: activeTab === 'size-conversion' },
            ]"
            @click="activeTab = 'size-conversion'"
          >
            Conversion
          </button>
          <button
            type="button"
            :class="[
              'sizeguide-tab',
              { active: activeTab === 'product-measurements' },
            ]"
            @click="activeTab = 'product-measurements'"
          >
            Body
          </button>
        </div>

        <!-- Size Conversion panel (toggle, not accordion) -->
        <div
          v-if="!isJewellery && activeTab === 'size-conversion'"
          class="sizeguide-panel"
        >
          <div class="size-conversion-container">
            <div class="sizeguide">
              <div class="sizeguide__row">
                <div class="sizeguide__cell sizeguide__cell--sticky">Size</div>
                <div class="sizeguide__cell">UK</div>
                <div class="sizeguide__cell">US</div>
                <div class="sizeguide__cell">AU</div>
                <div class="sizeguide__cell">EU</div>
              </div>
              <div class="sizeguide__row">
                <div class="sizeguide__cell sizeguide__cell--sticky">XXS</div>
                <div class="sizeguide__cell">4</div>
                <div class="sizeguide__cell">0</div>
                <div class="sizeguide__cell">4</div>
                <div class="sizeguide__cell">32</div>
              </div>
              <div class="sizeguide__row">
                <div class="sizeguide__cell sizeguide__cell--sticky">XS</div>
                <div class="sizeguide__cell">6</div>
                <div class="sizeguide__cell">2</div>
                <div class="sizeguide__cell">6</div>
                <div class="sizeguide__cell">34</div>
              </div>
              <div class="sizeguide__row">
                <div class="sizeguide__cell sizeguide__cell--sticky">S</div>
                <div class="sizeguide__cell">8</div>
                <div class="sizeguide__cell">4</div>
                <div class="sizeguide__cell">8</div>
                <div class="sizeguide__cell">36</div>
              </div>
              <div class="sizeguide__row">
                <div class="sizeguide__cell sizeguide__cell--sticky">M</div>
                <div class="sizeguide__cell">10</div>
                <div class="sizeguide__cell">6</div>
                <div class="sizeguide__cell">10</div>
                <div class="sizeguide__cell">38</div>
              </div>
              <div class="sizeguide__row">
                <div class="sizeguide__cell sizeguide__cell--sticky">L</div>
                <div class="sizeguide__cell">12</div>
                <div class="sizeguide__cell">8</div>
                <div class="sizeguide__cell">12</div>
                <div class="sizeguide__cell">40</div>
              </div>
              <div class="sizeguide__row">
                <div class="sizeguide__cell sizeguide__cell--sticky">XL</div>
                <div class="sizeguide__cell">14</div>
                <div class="sizeguide__cell">10</div>
                <div class="sizeguide__cell">14</div>
                <div class="sizeguide__cell">42</div>
              </div>
              <div class="sizeguide__row">
                <div class="sizeguide__cell sizeguide__cell--sticky">XXL</div>
                <div class="sizeguide__cell">16</div>
                <div class="sizeguide__cell">12</div>
                <div class="sizeguide__cell">16</div>
                <div class="sizeguide__cell">44</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Product Measurements panel -->
        <div
          v-show="isJewellery || activeTab === 'product-measurements'"
          class="sizeguide-panel"
        >
          <template v-if="isJewellery">
            <div class="sizeguide-panel__header">
              Ring Size Measurements (Inner Circumference)
            </div>
          </template>
          <div class="sizeguide-container sizeguide-container--fixed">
            <div class="sizeguide">
              <!-- Header row: Size + measurement columns -->
              <div class="sizeguide__row">
                <div
                  class="sizeguide__cell"
                  :class="{ 'sizeguide__cell--sticky': !isJewellery }"
                >
                  Size
                </div>
                <div
                  v-for="measurementKey in tableMeasurementKeys"
                  :key="measurementKey"
                  class="sizeguide__cell"
                >
                  {{ measurementKey }}
                </div>
              </div>
              <!-- Size rows -->
              <div
                v-for="size in sizes"
                :key="size.title"
                class="sizeguide__row"
              >
                <div class="sizeguide__cell sizeguide__cell--sticky">
                  <span>{{ size.title }}</span>
                </div>
                <div
                  v-for="measurementKey in tableMeasurementKeys"
                  :key="`${size.title}-${measurementKey}`"
                  class="sizeguide__cell"
                >
                  <span>{{
                    formatMeasurement(
                      getMeasurementValue(size.measurements, measurementKey)
                    ) || ""
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="unit-selector">
            <button
              :class="['unit-button', { active: selectedUnit === 'cm' }]"
              @click="selectedUnit = 'cm'"
            >
              cm
            </button>
            <button
              :class="['unit-button', { active: selectedUnit === 'in' }]"
              @click="selectedUnit = 'in'"
            >
              in
            </button>
          </div>
        </div>

        <!-- Measurement guide: stays visible regardless selected table -->
        <div v-if="!isJewellery" class="measurement-guide">
          <div v-if="sizeAdvisoryMessage" class="measurement-guide__advisory">
            <img
              :src="trueToSizeIcon"
              alt=""
              class="measurement-guide__advisory-icon"
            />
            <span>{{ sizeAdvisoryMessage }}</span>
          </div>
          <div class="measurement-guide__image-wrap">
            <img
              src="https://cdn.shopify.com/s/files/1/0942/9062/files/RatAndBoa_Giulia_Dress_2261_Flatlay_Front_1000x_crop_center_2x-removebg-preview_1_dcfdc208-ca49-4868-8054-a90d31fa9e2c.jpg?v=1778148198"
              alt="Measurement guide"
              class="measurement-guide__image"
            />
          </div>
          <div class="measurement-guide__list">
            <div class="measurement-guide__item">
              <p class="measurement-guide__label">BUST</p>
              <p>
                Wrap the tape around the fullest part of your bust and shoulder
                blades, then drop your arms to your sides to measure.
              </p>
            </div>
            <div class="measurement-guide__item">
              <p class="measurement-guide__label">WAIST</p>
              <p>
                Keeping the tape flush against the body, measure around your
                natural waistline at the navel.
              </p>
            </div>
            <div class="measurement-guide__item">
              <p class="measurement-guide__label">HIP</p>
              <p>
                Wrap the tape around the fullest part of your hip to measure.
                Inseam measurements vary by style and are noted in the item’s
                description.
              </p>
            </div>
          </div>
        </div>

        <div v-if="!isJewellery" class="whats-my-size-section">
          <button
            type="button"
            class="fit-quiz-trigger"
            @click.prevent="openFitQuiz"
          >
            Try our Fit Quiz
          </button>
        </div>

        <div class="contact-section">
          <p class="contact-header">NEED HELP?</p>
          <p class="contact-text">
            Unsure about the right size to choose? Email us at
          </p>
          <p class="contact-text">
            <a href="mailto:customercare@ratandboa.com"
              >customercare@ratandboa.com</a
            >
          </p>
          <!-- <div class="live-chat-row">
            <button type="button" class="live-chat-button">
              <Button
                id="live-chat"
                tag="a"
                form="underline-invert"
                theme="primary"
              >
                LIVE CHAT
              </Button>
              <img
                src="@/assets/icons/chat-bubble.png"
                class="chat-icon"
                alt=""
              />
            </button>
            <TimeSensitiveWrapper>
              <sup class="available">Available</sup>
            </TimeSensitiveWrapper>
          </div> -->
        </div>
      </div>
    </template>
  </SlideIn>
</template>

<script>
import SlideIn from "@/components/SlideIn.vue";
import cloneDeep from "lodash-es/cloneDeep";
import { mapState, mapActions } from "vuex";
import { unwrapNodes } from "@/helpers/graphql";
import trueToSizeIcon from "@/assets/icons/true-to-size.png";

export default {
  name: "SizeGuideSS25",
  components: {
    SlideIn,
  },
  data() {
    return {
      selectedUnit: "cm",
      activeTab: "size-conversion",
      trueToSizeIcon,
    };
  },
  computed: {
    ...mapState("product", ["product", "openSizeGuide"]),
    isJewellery() {
      return this.product?.productType === "Jewellery";
    },
    measurementKeys() {
      // Get all unique measurement keys from all sizes
      const keys = new Set();
      this.sizes.forEach((size) => {
        if (size.measurements) {
          size.measurements.forEach((measurement) => {
            keys.add(measurement.key);
          });
        }
      });
      return Array.from(keys).sort();
    },
    filteredMeasurementKeys() {
      // Filter measurement keys to only include those with actual values
      return this.measurementKeys.filter((key) =>
        this.hasMeasurementValues(key)
      );
    },
    tableMeasurementKeys() {
      if (this.isJewellery) {
        return this.filteredMeasurementKeys;
      }

      const preferredOrder = ["bust", "waist", "hip", "length"];
      const normalizedKeys = this.filteredMeasurementKeys.map((key) =>
        key.toLowerCase()
      );

      const orderedPreferred = preferredOrder.filter((key) =>
        normalizedKeys.includes(key)
      );

      const preferredKeys = orderedPreferred.map((key) =>
        this.filteredMeasurementKeys.find(
          (candidate) => candidate.toLowerCase() === key
        )
      );

      // Fallback: if metafield keys are not exact bust/waist/hip labels,
      // show all available measurement columns instead of rendering only "Size".
      if (!preferredKeys.length) {
        return this.filteredMeasurementKeys;
      }

      return preferredKeys;
    },
    sizes() {
      const sizes = [];
      const productVariants = unwrapNodes(this.product.variants?.edges) || [];

      console.log("Product variants:", productVariants);

      productVariants.forEach((variant) => {
        const metafields = cloneDeep(variant.sizes);
        console.log("Variant sizes for", variant.title, ":", metafields);

        const notNullMetafields = metafields.filter(
          (metafield) => metafield != null
        );
        const metafieldsSorted = notNullMetafields.sort((a, b) =>
          a.key > b.key ? 1 : -1
        );
        const title = variant.title.split(" / ")[0];

        sizes.push({
          title,
          measurements: metafieldsSorted,
        });
      });

      console.log("Processed sizes:", sizes);
      return sizes;
    },
    isOpen: {
      get() {
        return this.openSizeGuide;
      },
      set(val) {
        this.setOpenSizeGuide(val);
      },
    },
    sizeAdvisoryMessage() {
      const value = this.product?.trueToSize?.value;
      const advisoryMap = {
        0: "Runs small. We recommend sizing up two sizes from your usual.",
        1: "Fits true to size.",
        2: "Runs large. We recommend sizing down from your usual.",
      };

      return advisoryMap[String(value)] || "";
    },
  },
  watch: {
    isOpen(val) {
      if (!val || this.isJewellery) return;

      // Keep EasySize bindings fresh when the drawer opens so Fit Quiz
      // can fall back to a local re-bind if needed.
      this.$nextTick(() => {
        if (typeof window.easy_size?.start === "function") {
          window.easy_size.start();
        }
      });
    },
  },
  methods: {
    ...mapActions("product", ["setOpenSizeGuide"]),
    openFitQuiz() {
      // Prefer a Fit Quiz button EasySize bound on page load (PDP / variants).
      const boundButton = document.querySelector(".easysize_button");

      if (boundButton) {
        this.setOpenSizeGuide(false);
        this.$nextTick(() => {
          boundButton.click();
        });
        return;
      }

      if (typeof window.easy_size?.start === "function") {
        window.easy_size.start();
        this.$nextTick(() => {
          document.querySelector(".easysize_button")?.click();
        });
      }
    },
    formatMeasurement(value) {
      if (!value || isNaN(parseFloat(value))) return value || "";
      const numValue = parseFloat(value);
      if (this.selectedUnit === "in") {
        return (numValue / 2.54).toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 1,
        });
      }
      return numValue.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    getMeasurementValue(measurements, key) {
      if (!measurements || !Array.isArray(measurements)) return null;
      const measurement = measurements.find((m) => m.key === key);
      return measurement ? measurement.value : null;
    },
    hasMeasurementValues(measurementKey) {
      return this.sizes.some((size) => {
        const value = this.getMeasurementValue(
          size.measurements,
          measurementKey
        );
        return (
          value &&
          value !== "" &&
          !isNaN(parseFloat(value)) &&
          parseFloat(value) !== 0
        );
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.content-container-inner {
  padding: 0 20px 20px;
  @include respond-above(md) {
    padding: 0 40px 20px;
  }
}

/* Tabs: Product Measurements | Size Conversion */
.sizeguide-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 0;
  width: 100%;
  border-bottom: 1px solid $color-grey-light;
}

.sizeguide-tab {
  flex: 1;
  padding: 0 10px 12px;
  border: none;
  background: transparent;
  @include editorial-body(14px);
  cursor: pointer;
  color: $color-black;
  white-space: nowrap;
  position: relative;

  &.active {
    &::after {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      bottom: -1px;
      height: 1px;
      background-color: $color-black;
    }
  }
}

.sizeguide-panel {
  padding-top: 20px;

  &__header {
    @include utility-body(15px);
    margin-bottom: 12px;
  }
}

.sizeguide-container,
.size-conversion-container {
  overflow-x: auto;
  margin-bottom: 10px;
  -webkit-overflow-scrolling: touch;
}

.sizeguide-container--fixed {
  overflow-x: hidden;
}

.sizeguide-container--fixed .sizeguide {
  min-width: 0;
  width: 100%;
}

.sizeguide-container--fixed .sizeguide__row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  gap: 2px;
}

.sizeguide-container--fixed .sizeguide__cell {
  width: auto;
  min-width: 0;
}

.sizeguide-container--fixed .sizeguide__cell--sticky {
  position: static;
}

.sizeguide {
  min-width: max-content;
  margin-bottom: 20px;

  &__row {
    display: flex;
    gap: 2px;
    margin-bottom: 2px;
  }

  &__cell {
    background-color: #fafafa;
    border-radius: 4px;
    @include editorial-body(12px);
    text-align: center;
    width: 104px;
    height: 44px;
    align-items: center;
    display: grid;

    span {
      text-transform: capitalize;
    }
  }

  &__cell--sticky {
    position: sticky;
    left: 0;
    z-index: 1;
    background-color: #fafafa;
  }
}

.unit-selector {
  display: flex;
  gap: 0;
  margin: 10px 10px 20px 0;
  background: #f5f5f5;
  border-radius: 20px;
  padding: 2px;
  width: fit-content;
}

.unit-button {
  padding: 8px 16px;
  border: none;
  background: transparent;
  border-radius: 18px;
  font-size: 12px;
  @include utility-body(15px);
  cursor: pointer;
  transition: all 0.3s ease;
  color: #333;

  &.active {
    background-color: white;
    color: #333;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.5);
  }
}

.measurement-guide {
  margin: 28px 0 35px 0;
  /* Match body copy elsewhere in the drawer (avoid #000 on labels only). */
  color: $color-grey-darkest;
}

.measurement-guide__list {
  text-align: left;
  margin-top: 24px;
}

.measurement-guide__item {
  margin: 0 0 40px 0;

  p {
    @include editorial-body(14px);
    text-transform: none;
    line-height: 1.5;
    margin-bottom: 10px;
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.measurement-guide__label {
  @include editorial-body(14px);
  text-transform: uppercase;
  margin: 0 0 8px;
}

.measurement-guide__advisory {
  background: #f5f5f5;
  border-radius: 4px;
  padding: 16px 20px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  @include editorial-body(14px);
  font-style: italic;
  line-height: 1.6;
}

.measurement-guide__advisory-icon {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  margin-top: 2px;
}

.measurement-guide__image-wrap {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  margin: 40px 0 16px;
  background: #fafafa;
  padding: 24px 0;
}

.measurement-guide__image {
  width: 350px;
  height: 525px;
  max-width: 100%;
  display: block;
  object-fit: cover;
}

.whats-my-size-section {
  padding: 15px 0;
  border-top: 1px solid $color-grey-light;
  border-bottom: 1px solid $color-grey-light;
  @include respond-above(md) {
    padding: 15px 0;
  }
}

.fit-quiz-trigger {
  appearance: none;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: $color-black;
  @include utility-body(15px);
  text-transform: none;
  position: relative;
  display: inline-block;

  &::after {
    @include editorial-dotted-underline($color-black);
  }
}

.contact-section {
  padding: 20px 0;
  @include respond-above(md) {
    padding: 20px 0;
  }
}

.contact-header {
  @include editorial-body(14px);
  text-transform: uppercase;
  margin-bottom: 10px;
}

.contact-text {
  @include editorial-body(14px);

  a {
    position: relative;
    display: inline-block;
    color: inherit;
    text-decoration: none;

    &::after {
      @include editorial-dotted-underline($color-black);
    }
  }

  :deep(.button) {
    background: none;
    padding: 0;
    color: $color-black;
    @include editorial-body(14px);
    text-decoration: none;
    text-transform: none;

    &.button--underline .text::after {
      @include editorial-dotted-underline($color-black);
    }
  }
}

.live-chat-row {
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  @include respond-above(md) {
    margin-top: 10px;
  }
}

.live-chat-button {
  padding: 12px 20px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  border: 1px solid $color-black;
  background-color: $color-black;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  .chat-icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  :deep(.button) {
    background: none;
    padding: 0;
    color: $color-white;
    @include utility-body(14px);
    text-transform: none;
    letter-spacing: 0.04em;
  }

  &:hover {
    background-color: $color-white;
    border-color: $color-black;

    :deep(.button) {
      color: $color-black;
    }

    .chat-icon {
      filter: invert(1);
    }
  }
}

.available {
  color: $color-green;
  @include utility-label(10px);
}
</style>
